/* 名前入力欄2(コンポーネント内で姓と名を分割する) */
import React, { useState } from 'react';
import { Form, Col, Row } from 'react-bootstrap';
import RowTemplate from '../atoms/RowTemplate';
import Messages from '../molecules/Messages';
import { name_splitter, key_code_list } from '../Const';
import Paragraph from './Paragraph';
import { isBrowserIe, isBrowserIOSSafari, isBrowserIpadSafari } from '../BrowserJudgment';
import Heading from './Heading';

interface Props {
  id: string;
  label: string;
  need?: boolean;
  value: string;
  name: string;
  placeholder: string;
  maxlength?: number;
  handleChangeFirstName: (value: string) => void;
  handleChangeLastName: (value: string) => void;
  message?: string;
  help_msg?: string;
  disabled: boolean;
  looks_inactive?: boolean;
  handleBlurFirstName: VoidFunction;
  handleBlurLastName: VoidFunction;
  readonly?: boolean;
  heading?: string;
}

const NameInput2: React.FC<Props> = ({
  id,
  label,
  need,
  name,
  value,
  placeholder,
  maxlength,
  handleChangeFirstName,
  handleChangeLastName,
  message,
  help_msg,
  disabled,
  looks_inactive,
  handleBlurFirstName,
  handleBlurLastName,
  readonly,
  heading,
}) => {
  const [is_ban_space, setIsBanSpace] = useState(false);

  // 引数valを姓と名に分割する
  const sliceValue = (val: string) => {
    if (val === null) {
      return ['', ''];
    }

    const name = [];
    const sp = val.split(name_splitter);
    name[0] = sp[0];
    name[1] = sp.slice(1, sp.length).join(name_splitter);

    // nameの要素数が2つ未満ならば要素数が2つになるように空文字をプッシュする
    if (name === null || typeof name === 'undefined' || name.length < 2) {
      name.push('');
    }

    return name;
  };

  // 姓、入力時のイベント
  const onChangeLastName = (event: React.ChangeEvent<HTMLInputElement>) => {
    // 半角スペースを拒否している場合は早期リターンをする
    if (is_ban_space) {
      setIsBanSpace(false);
      return;
    }

    // スペースが含まれる場合は取り除く
    if (event.target.value.match(/[\s]/g)) {
      event.target.value = event.target.value.replace(/[\s]/g, '');
    }

    const last = event.target.value;
    // undefinedの場合は空文字を出力する
    const first = typeof sliceValue(value)[1] !== 'undefined' ? sliceValue(value)[1] : '';
    // 姓と名を文字列連結する
    handleChangeLastName(`${last}${name_splitter}${first}`);
  };
  // 名、入力時のイベント
  const onChangeFirstName = (event: React.ChangeEvent<HTMLInputElement>) => {
    // 半角スペースを拒否している場合は早期リターンをする
    if (is_ban_space) {
      setIsBanSpace(false);
      return;
    }

    // スペースが含まれる場合は取り除く
    if (event.target.value.match(/[\s]/g)) {
      event.target.value = event.target.value.replace(/[\s]/g, '');
    }

    // undefinedの場合は空文字を出力する
    const last = typeof sliceValue(value)[0] !== 'undefined' ? sliceValue(value)[0] : '';
    const first = event.target.value;
    // 姓と名を文字列連結する
    handleChangeFirstName(`${last}${name_splitter}${first}`);
  };

  /**
   * ラベルを描画する
   * @return JSX.Element ラベルのElement
   */
  const returnLabelJsx = () => {
    return (
      <>
        {heading && <Heading label={heading} size={'s'} />}
        <Paragraph label={`${sliceValue(value)[0]}${name_splitter}${sliceValue(value)[1]}`} />
      </>
    );
  };

  /**
   * KeyDownイベントの処理
   * @param event キーボードイベント
   */
  const onKeyDownCapture = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (isBrowserIe() && event.keyCode === key_code_list.space) {
      // IEの場合はkeyCodeで半角スペースを検知する
      setIsBanSpace(true);
    } else if (isBrowserIOSSafari() && event.key.match(/[\s]/)) {
      // iPhone Safariの場合は正規表現で検知する
      setIsBanSpace(true);
    } else if (isBrowserIpadSafari() && event.key.match(/[\s]/)) {
      // iPad Safariの場合は正規表現で検知する
      setIsBanSpace(true);
    } else if (!isBrowserIe() && event.key === name_splitter) {
      // 上記以外は半角スペースと一致するかで検知する
      setIsBanSpace(true);
    }
  };

  /**
   * 通常のNameInput2を描画する
   * @return JSX.Element NameInput2のElement
   */
  const returnNameInput2Jsx = () => {
    return (
      <>
        {heading && <Heading label={heading} size={'s'} />}
        <Row>
          <Col xs={5} className="pr-1">
            <Form.Control
              id={`${id}_last`}
              type="text"
              name={`${name}_last`}
              onChange={onChangeLastName}
              onKeyDownCapture={onKeyDownCapture}
              value={sliceValue(value)[0]}
              placeholder={sliceValue(placeholder)[0]}
              disabled={disabled || readonly}
              onBlur={handleBlurLastName}
              maxLength={maxlength}
              className={message && 'textarea-error'}
            />
          </Col>
          <Col xs={5} className="pl-1">
            <Form.Control
              id={`${id}_first`}
              type="text"
              name={`${name}_first`}
              onChange={onChangeFirstName}
              onKeyDownCapture={onKeyDownCapture}
              value={sliceValue(value)[1]}
              placeholder={sliceValue(placeholder)[1]}
              disabled={disabled || readonly}
              onBlur={handleBlurFirstName}
              maxLength={maxlength}
              className={message && 'textarea-error'}
            />
          </Col>
        </Row>
        <Messages help_msg={help_msg} valid_msg={message} />
      </>
    );
  };

  return (
    <RowTemplate
      label={label}
      need={need}
      disabled={disabled}
      looks_inactive={looks_inactive}
      content={disabled || looks_inactive ? returnLabelJsx() : returnNameInput2Jsx()}
    />
  );
};

export default NameInput2;
